import { useEffect, useState } from 'react';
import type { GlobalProfileView } from '@shared';
import { formatCredits } from '../../lib/format';
import { request } from '../../lib/spoylApi';
import { Icon } from '../ui/Icon';
import styles from './GlobalStatus.module.css';

/**
 * The player's general profile: what they have spent across **every** realm in
 * this server, and the server-wide status it earns.
 *
 * It sits beside the realm's own battle pass rather than instead of it. A realm's
 * pass says how far you have come with one creator and what it unlocks; this says
 * how far you have come with all of them, and it is the one that becomes a role in
 * the community server. Shown only where there is more than one realm.
 *
 * Own figures only, in points — the same unit every realm ladder shows, and never
 * in currency: Spoyl shows a player no price.
 */
export function GlobalStatus() {
  const [view, setView] = useState<GlobalProfileView | null>(null);

  useEffect(() => {
    let cancelled = false;
    request<{ view: GlobalProfileView }>('/global')
      .then((answer) => {
        if (!cancelled) setView(answer.view);
      })
      // A card that cannot load simply does not appear: it is a second opinion
      // on Home, never something the page waits for.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // A server with no tiers has nothing to show.
  if (!view || view.track.length === 0) return null;

  const { total, tier, next, realms } = view;
  const floor = tier?.threshold ?? 0;
  const span = next ? next.threshold - floor : 1;
  const percent = next
    ? Math.max(0, Math.min(100, Math.round(((total - floor) / span) * 100)))
    : 100;

  return (
    <section className={styles.card}>
      <div className={styles.head}>
        <span className={styles.tier}>
          <Icon name="crown" size={14} />
          {tier?.name ?? 'No status yet'}
        </span>
        <span className={styles.scope}>Across all realms</span>
      </div>

      {next ? (
        <div className={styles.gap}>
          <strong className={styles.amount}>{formatCredits(next.threshold - total)} points</strong>{' '}
          to <strong>{next.name}</strong>
        </div>
      ) : (
        <div className={styles.gap}>
          <strong className={styles.amount}>{formatCredits(total)} points</strong> — the top of the
          ladder.
        </div>
      )}

      <div className={styles.bar}>
        <span className={styles.fill} style={{ width: `${percent}%` }} />
      </div>

      {realms.length > 0 && (
        <p className={styles.realms}>
          {realms.map((realm) => `${realm.profileName} ${formatCredits(realm.spend)}`).join(' · ')}
        </p>
      )}
    </section>
  );
}
